/**
 * Dates, numbers and prices for people to read (web plan W3.0): `Intl` with
 * the route locale's formatting locale (`en-PK` on `/pk/en`), its country's
 * time zone and its currency — never the browser's locale or time zone, so a
 * visitor abroad (and the SEO server, whatever its TZ) sees Pakistan's
 * calendar day on the Pakistan site.
 *
 * Titles, descriptions and other head text do NOT use these: they keep the
 * fixed English month names of `@/seo/text` (`Sep`, never the `Sept` that
 * CLDR's en-PK prints), which the SEO server and the SPA must produce byte for
 * byte, whatever ICU data each runtime ships.
 *
 * The time zone and currency come from `@/seo/locales` (PK: Asia/Karachi,
 * PKR). TODO(backend): read them from the public `countries` query once its
 * `Country` type exposes `timeZone` and `currency` (the backend model has
 * both since BG1; the public SDL has only `code` and `name`).
 */
import { DEFAULT_LOCALE, type Locale } from '@/seo/locales';
import { formattingLocale } from './index';

type DateValue = Date | string | number | null | undefined;

/** A Date for an ISO string, epoch millis (number or digit string) or Date; null when invalid. */
export function toDate(value: DateValue): Date | null {
  if (value === null || value === undefined || value === '') return null;
  let date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime()) && typeof value === 'string' && /^\d+$/.test(value)) {
    date = new Date(Number(value));
  }
  return Number.isNaN(date.getTime()) ? null : date;
}

const cache = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>();

function cached<T extends Intl.DateTimeFormat | Intl.NumberFormat>(key: string, make: () => T): T {
  let formatter = cache.get(key) as T | undefined;
  if (!formatter) {
    formatter = make();
    cache.set(key, formatter);
  }
  return formatter;
}

/** The default date style: day, short month, year ("6 Sept 2025" in en-PK's CLDR data). */
export const DATE_STYLE: Readonly<Intl.DateTimeFormatOptions> = Object.freeze({
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/**
 * A date in the locale's formatting locale and time zone; `''` for no or an
 * invalid date. `options` may change the fields shown, never the zone.
 */
export function formatDate(
  value: DateValue,
  locale: Locale = DEFAULT_LOCALE,
  options: Intl.DateTimeFormatOptions = DATE_STYLE,
): string {
  const date = toDate(value);
  if (!date) return '';
  const tag = formattingLocale(locale);
  const formatter = cached(`d|${tag}|${locale.timeZone}|${JSON.stringify(options)}`, () =>
    new Intl.DateTimeFormat(tag, { ...options, timeZone: locale.timeZone }),
  );
  return formatter.format(date);
}

/** A number in the locale's formatting locale ("1,234"); `''` for a non-finite value. */
export function formatNumber(
  value: number | null | undefined,
  locale: Locale = DEFAULT_LOCALE,
  options: Intl.NumberFormatOptions = {},
): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const tag = formattingLocale(locale);
  return cached(`n|${tag}|${JSON.stringify(options)}`, () => new Intl.NumberFormat(tag, options)).format(value);
}

/**
 * A clock time given as 24-hour "HH:MM" (opening hours, backend B3.4) in the
 * locale's style ("10:00 am", "10:30 pm" in en-PK); "24:00" reads as
 * midnight. The time is local already, so no time zone applies (formatted in
 * UTC on purpose). `''` for anything else.
 */
export function formatClockTime(value: string | null | undefined, locale: Locale = DEFAULT_LOCALE): string {
  const match = /^([01]\d|2[0-4]):([0-5]\d)$/.exec(value ?? '');
  if (!match || (match[1] === '24' && match[2] !== '00')) return '';
  const tag = formattingLocale(locale);
  const formatter = cached(`t|${tag}`, () =>
    new Intl.DateTimeFormat(tag, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }),
  );
  return formatter.format(Date.UTC(1970, 0, 1, Number(match[1]) % 24, Number(match[2])));
}

const WEEKDAY_INDEX: Readonly<Record<string, number>> = Object.freeze({
  MON: 0,
  TUE: 1,
  WED: 2,
  THU: 3,
  FRI: 4,
  SAT: 5,
  SUN: 6,
});

/** A backend `Weekday` (MON … SUN) as the locale names it ("Monday"; `style` 'short' → "Mon"); `''` for an unknown day. */
export function weekdayName(
  day: string,
  locale: Locale = DEFAULT_LOCALE,
  style: 'long' | 'short' = 'long',
): string {
  if (!Object.hasOwn(WEEKDAY_INDEX, day)) return '';
  const index = WEEKDAY_INDEX[day];
  const tag = formattingLocale(locale);
  const formatter = cached(`w|${tag}|${style}`, () =>
    new Intl.DateTimeFormat(tag, { weekday: style, timeZone: 'UTC' }),
  );
  // 1 Jan 2024 was a Monday.
  return formatter.format(Date.UTC(2024, 0, 1 + index));
}

/** A price in the locale's currency ("Rs 1,500" for PKR in en-PK); `''` for a non-finite amount. */
export function formatPrice(
  amount: number | null | undefined,
  locale: Locale = DEFAULT_LOCALE,
  options: Omit<Intl.NumberFormatOptions, 'style' | 'currency'> = {},
): string {
  return formatNumber(amount, locale, { ...options, style: 'currency', currency: locale.currency });
}
